import { useState, useEffect } from 'react';
import { cn } from '@/lib/utils/utils';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Field, FieldLabel, FieldDescription } from '@/components/ui/field';
import { CreditCard, Gift } from 'lucide-react';
import { horodatyApi } from '@/lib/utils/horodaty-api';
import { SubmitButton } from '@/components/ui/app/submit-button';
import { useTranslation } from 'react-i18next';
import i18n from '@/lib/i18n';

function getAccountTypes() {
    return [
        { value: 'individuals', label: i18n.t('account:particuliers'), apiKey: 'user' },
        { value: 'companies', label: i18n.t('account:entreprises'), apiKey: 'company' },
    ];
}

const DEFAULT_FORM_STATE = {
    cardEnabled: true,
    wireEnabled: true,
    welcomeTokenCount: '3',
    restitutionDelay: '30',
};

function apiParamsToForm(params) {
    if (!params) return { ...DEFAULT_FORM_STATE };
    return {
        cardEnabled: params.card_payment_enabled ?? true,
        wireEnabled: params.wire_transfer_enabled ?? true,
        welcomeTokenCount: String(params.welcome_token_count ?? 3),
        restitutionDelay: String(params.restitution_delay_days ?? 30),
    };
}

function formToApiPayload(form) {
    return {
        card_payment_enabled: form.cardEnabled,
        wire_transfer_enabled: form.wireEnabled,
        welcome_token_count: parseInt(form.welcomeTokenCount, 10) || 0,
        restitution_delay_days: parseInt(form.restitutionDelay, 10) || 0,
    };
}

function TokenForm({ state, onChange }) {
    const { t } = useTranslation();
    return (
        <div className="space-y-5">
            {/* Payment Methods */}
            <div className="space-y-4 rounded-[12px] p-5 border border-border">
                <div className="flex items-center gap-3">
                    <div className="bg-muted rounded-full h-9 w-9 flex items-center justify-center shrink-0">
                        <CreditCard className="h-5 w-5 text-foreground" />
                    </div>
                    <h3 className="text-base font-semibold text-foreground">{t('account:modesDePaiementDisponibles')}</h3>
                </div>

                <div className="space-y-3 pl-1">
                    <label className="flex items-center gap-3 cursor-pointer bg-muted p-[12px] rounded-[8px] hover:bg-muted-hover transition-colors">
                        <Checkbox
                            checked={state.cardEnabled}
                            onCheckedChange={(v) => onChange({ ...state, cardEnabled: v })}
                        />
                        <span className="text-sm text-foreground">{t('account:carteBancaire')}</span>
                    </label>

                    <label className="flex items-center gap-3 cursor-pointer bg-muted p-[12px] rounded-[8px] hover:bg-muted-hover transition-colors">
                        <Checkbox
                            checked={state.wireEnabled}
                            onCheckedChange={(v) => onChange({ ...state, wireEnabled: v })}
                        />
                        <span className="text-sm text-foreground">{t('account:virementBancaire')}</span>
                    </label>
                </div>
            </div>

            {/* Welcome Tokens */}
            <div className="space-y-5 rounded-[12px] p-5 border border-border">
                <div className="flex items-center gap-3">
                    <div className="bg-muted rounded-full h-9 w-9 flex items-center justify-center shrink-0">
                        <Gift className="h-5 w-5 text-foreground" />
                    </div>
                    <h3 className="text-base font-semibold text-foreground">{t('account:jetonsDeBienvenue')}</h3>
                </div>

                {/* Welcome token count */}
                <Field>
                    <FieldLabel className="text-[13px] font-medium">
                        {t('account:nombreDeJetonsOffertsAuxNouveauxUtilisateurs')}
                    </FieldLabel>
                    <Input
                        type="number"
                        min="0"
                        value={state.welcomeTokenCount}
                        onChange={(e) => onChange({ ...state, welcomeTokenCount: e.target.value })}
                    />
                    <FieldDescription className="text-xs">
                        {t('account:cesJetonsSerontAutomatiquementAjoutesAuCompteLor')}
                    </FieldDescription>
                </Field>

                {/* Restitution delay - masqué pour l'instant */}
                {/* <Field>
                    <FieldLabel className="text-[13px] font-medium">
                        Délai de restitution des jetons de bienvenue (en jours)
                    </FieldLabel>
                    <Input
                        type="number"
                        min="0"
                        value={state.restitutionDelay}
                        onChange={(e) => onChange({ ...state, restitutionDelay: e.target.value })}
                    />
                    <FieldDescription className="text-xs">
                        Les jetons de bienvenue seront automatiquement restitués si le compte n'est pas utilisé pendant
                        ce délai. Définir à 0 pour désactiver cette fonctionnalité.
                    </FieldDescription>
                </Field> */}
            </div>
        </div>
    );
}

export default function TokenManagementContent() {
    const { t } = useTranslation();
    const ACCOUNT_TYPES = getAccountTypes();
    const [accountType, setAccountType] = useState('individuals');
    const [individualsForm, setIndividualsForm] = useState({ ...DEFAULT_FORM_STATE });
    const [companiesForm, setCompaniesForm] = useState({ ...DEFAULT_FORM_STATE });
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        horodatyApi.adminParameters
            .get()
            .then((res) => {
                const data = res.data?.data ?? {};
                if (data.user) setIndividualsForm(apiParamsToForm(data.user));
                if (data.company) setCompaniesForm(apiParamsToForm(data.company));
            })
            .catch(() => {})
            .finally(() => setLoading(false));
    }, []);

    const currentTab = ACCOUNT_TYPES.find((t) => t.value === accountType);
    const currentForm = accountType === 'individuals' ? individualsForm : companiesForm;
    const setCurrentForm = accountType === 'individuals' ? setIndividualsForm : setCompaniesForm;

    function handleSave() {
        if (!currentTab) return;
        setSaving(true);
        horodatyApi.adminParameters
            .update(currentTab.apiKey, formToApiPayload(currentForm))
            .catch(() => {})
            .finally(() => setSaving(false));
    }

    return (
        <div className="space-y-5">
            {/* Page Header */}
            <div>
                <h2 className="text-base font-semibold text-foreground">{t('account:gestionDesJetons')}</h2>
                <p className="text-[13px] text-muted-foreground mt-0.5">
                    {t('account:configurezLesModesDePaiementEtLesParametres')}
                </p>
            </div>

            {/* Account Type Switcher */}
            <div className="bg-surface rounded-xl p-1 flex gap-1 w-full max-w-[320px]">
                {ACCOUNT_TYPES.map((tab) => (
                    <Button
                        key={tab.value}
                        variant="plain"
                        color="neutral"
                        size="xlg"
                        onClick={() => setAccountType(tab.value)}
                        className={cn(
                            'flex-1 text-[13px]',
                            accountType === tab.value
                                ? 'bg-card text-foreground shadow-sm hover:bg-card'
                                : 'text-muted-foreground',
                        )}
                    >
                        {tab.label}
                    </Button>
                ))}
            </div>

            {/* Tab Content */}
            {!loading && <TokenForm state={currentForm} onChange={setCurrentForm} />}

            {/* Save Button */}
            <div className="flex justify-end">
                <SubmitButton onClick={handleSave} disabled={saving || loading}>
                    {saving ? t('account:enregistrement') : t('account:enregistrer')}
                </SubmitButton>
            </div>
        </div>
    );
}
